/**
 * MatchView — соответствия пар (kind="match").
 *
 * Левый столбец `kindData.left` фиксирован; для каждого левого элемента игрок
 * выбирает соответствие из правого столбца `kindData.right` через `<select>`
 * (простой и надёжный UI без drag-and-drop). Когда все пары определены,
 * активна кнопка «Ответить» → onAnswer({kind:'match', data:{pairs}}).
 *
 * На reveal показывается эталонное соответствие; верные пары игрока
 * помечаются зелёным, ошибочные — красным. Spectate (ведущий, Фаза 10 И3):
 * read-only сетка пар + пул вариантов; после reveal — эталонные пары.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isMatchReveal, matchColumns, type PlayerAnswer } from '../../protocol';
import type { QuestionBodyProps } from './types';
import { FormulaText } from '../FormulaText';

const PLACEHOLDER = '__none__';

export function MatchView({
  question,
  variant,
  myAnswer,
  reveal,
  onAnswer,
}: QuestionBodyProps): JSX.Element {
  const { t } = useTranslation();
  const { left, right } = matchColumns(question);

  const buildInitial = (): Record<number, string> => {
    const picks: Record<number, string> = {};
    if (myAnswer?.kind === 'match') {
      myAnswer.data.pairs.forEach((p, i) => {
        if (i < left.length) picks[i] = p.right;
      });
    }
    return picks;
  };
  const [picks, setPicks] = useState<Record<number, string>>(buildInitial);
  const [submitted, setSubmitted] = useState(myAnswer?.kind === 'match');

  const locked = variant === 'answer' && (submitted || reveal !== null);
  const revealData = reveal?.reveal && isMatchReveal(reveal.reveal) ? reveal.reveal : null;
  const correctMap = new Map<string, string>();
  if (revealData) for (const p of revealData.pairs) correctMap.set(p.left, p.right);

  const allFilled = left.length > 0 && left.every((_, i) => Boolean(picks[i]));

  function selectOption(i: number, value: string): void {
    if (locked) return;
    setPicks((prev) => ({ ...prev, [i]: value }));
  }

  function handleSubmit(): void {
    if (locked || !allFilled) return;
    setSubmitted(true);
    const pairs = left.map((l, i) => ({ left: l, right: picks[i] }));
    const answer: PlayerAnswer = { kind: 'match', data: { pairs } };
    onAnswer(answer);
  }

  if (variant === 'spectate') {
    // Баг-О (Фаза 10, И3): read-only предпросмотр задания для ведущего — те же
    // строки пар, что у игрока, но без интерактива; после reveal в правом
    // столбце — эталон из correctMap. Анти-чит: до reveal хост видит только
    // kindData (столбцы без верной связи), эталон приходит лишь с reveal.
    return (
      <div className="match-view">
        {left.map((leftItem, i) => {
          const correctRight = correctMap.get(leftItem);
          const isRevealed = revealData !== null && correctRight !== undefined;
          return (
            <div key={i} className="match-view__row">
              <FormulaText className="match-view__left" text={leftItem} />
              <span className="match-view__arrow" aria-hidden="true">
                ↔
              </span>
              <span className="match-view__select" data-testid={`match-pair-${i}`}>
                {isRevealed ? <FormulaText text={correctRight} /> : '—'}
              </span>
            </div>
          );
        })}
        <p className="q-hint small" data-testid="match-pool">
          {t('match.poolHint')}{' '}
          {right.map((r, j) => (
            <span key={j}>
              {j > 0 ? ' · ' : ''}
              <FormulaText text={r} />
            </span>
          ))}
        </p>
      </div>
    );
  }

  return (
    <div className="match-view">
      {left.map((leftItem, i) => {
        const chosen = picks[i] ?? PLACEHOLDER;
        const correctRight = correctMap.get(leftItem);
        const isRevealed = revealData !== null;
        const matched = isRevealed && chosen === correctRight;
        const wrong = isRevealed && chosen !== correctRight && chosen !== PLACEHOLDER;
        const classes = ['match-view__row'];
        if (matched) classes.push('match-view__row--correct');
        if (wrong) classes.push('match-view__row--wrong');
        return (
          <div key={i} className={classes.join(' ')}>
            <FormulaText className="match-view__left" text={leftItem} />
            <span className="match-view__arrow" aria-hidden="true">
              ↔
            </span>
            <select
              className="match-view__select"
              value={chosen}
              disabled={locked}
              onChange={(e) => selectOption(i, e.target.value)}
              aria-label={t('match.selectAria', { item: leftItem })}
              data-testid={`match-select-${i}`}
            >
              <option value={PLACEHOLDER} disabled>
                {t('match.choose')}
              </option>
              {right.map((r, j) => (
                <option key={j} value={r}>
                  {r}
                </option>
              ))}
            </select>
            {isRevealed && correctRight !== undefined ? (
              <span className="match-view__verdict">
                {matched ? (
                  '✓'
                ) : (
                  <FormulaText text={t('match.verdictWrong', { right: correctRight })} />
                )}
              </span>
            ) : null}
          </div>
        );
      })}

      <div className="open-view__row">
        {submitted && !revealData ? (
          <span className="q-hint q-hint--ok">{t('open.answered')}</span>
        ) : null}
        <button
          type="button"
          className="cta"
          disabled={locked || !allFilled}
          onClick={handleSubmit}
        >
          {t('common.answer')}
        </button>
      </div>
    </div>
  );
}
